/**
 * RabbitMQ message contracts. Every message travels in the same envelope and
 * is routed on the `klunkers` topic exchange by its `type`.
 *
 * Versioning rule: a change that an existing consumer could not parse needs a
 * new `version` (and the consumer keeps accepting the old one until every
 * producer has moved). Adding an optional payload field does not.
 */
import { z } from 'zod'

export const EXCHANGE = 'klunkers'
export const DEAD_LETTER_EXCHANGE = 'klunkers.dlx'

const envelope = <T extends string, P extends z.ZodType>(type: T, payload: P) =>
  z.object({
    id: z.uuid(),
    type: z.literal(type),
    version: z.literal(1),
    user_id: z.uuid(),
    enqueued_at: z.iso.datetime(),
    payload,
  })

/** Ask media-agent to derive display and thumbnail sizes for an uploaded photo. */
export const MediaProcessMessage = envelope(
  'media.process',
  z.object({
    photo_id: z.uuid(),
    object_key: z.string().min(1),
  }),
)
export type MediaProcessMessage = z.infer<typeof MediaProcessMessage>

/** media-agent finished (or gave up on) a photo. The API relays it to open clients. */
export const PhotoProcessedMessage = envelope(
  'photo.processed',
  z.object({
    photo_id: z.uuid(),
    owner_type: z.string(),
    owner_id: z.uuid(),
    state: z.enum(['ready', 'failed']),
  }),
)
export type PhotoProcessedMessage = z.infer<typeof PhotoProcessedMessage>

/**
 * Something that affects a rider's wear or intervals changed (a ride was
 * logged, moved or deleted; an interval was added, edited or reset).
 * reminder-agent re-evaluates that rider's intervals.
 */
export const UsageChangedMessage = envelope(
  'usage.changed',
  z.object({ reason: z.enum(['ride', 'interval', 'install', 'service', 'schedule']) }),
)
export type UsageChangedMessage = z.infer<typeof UsageChangedMessage>

/** reminder-agent wrote an in-app notification; the API relays it to open apps. */
export const NotificationCreatedMessage = envelope(
  'notification.created',
  z.object({
    notification_id: z.uuid(),
    title: z.string(),
    body: z.string(),
    link: z.string().nullable(),
  }),
)
export type NotificationCreatedMessage = z.infer<typeof NotificationCreatedMessage>

/** Read the text (a frame number) from a photo tagged 'serial'. */
export const MediaOcrMessage = envelope('media.ocr', z.object({ photo_id: z.uuid() }))
export type MediaOcrMessage = z.infer<typeof MediaOcrMessage>

/** A voice note was attached to a capture; capture-agent may transcribe it. */
export const CaptureAudioMessage = envelope(
  'capture.audio',
  z.object({ capture_id: z.uuid(), object_key: z.string().min(1) }),
)
export type CaptureAudioMessage = z.infer<typeof CaptureAudioMessage>

/** Ask export-agent to render a document (e.g. a service-history PDF). */
export const ExportRequestedMessage = envelope(
  'export.requested',
  z.object({ export_id: z.uuid() }),
)
export type ExportRequestedMessage = z.infer<typeof ExportRequestedMessage>

/** export-agent finished (or gave up on) an export. The API relays it to open clients. */
export const ExportReadyMessage = envelope(
  'export.ready',
  z.object({ export_id: z.uuid(), bike_id: z.uuid(), state: z.enum(['ready', 'failed']) }),
)
export type ExportReadyMessage = z.infer<typeof ExportReadyMessage>

/** Ask ai-agent to work on a suggestion (split a note, identify a part, diagnose). */
export const AiRequestedMessage = envelope('ai.requested', z.object({ suggestion_id: z.uuid() }))
export type AiRequestedMessage = z.infer<typeof AiRequestedMessage>

/** ai-agent finished a suggestion. The API relays it to open clients. */
export const AiSuggestionReadyMessage = envelope(
  'ai.suggestion.ready',
  z.object({
    suggestion_id: z.uuid(),
    kind: z.enum(['split_note', 'identify_part', 'diagnose']),
    subject_id: z.uuid(),
    state: z.enum(['done', 'failed', 'unavailable']),
  }),
)
export type AiSuggestionReadyMessage = z.infer<typeof AiSuggestionReadyMessage>

/**
 * A Strava webhook event, for ride-sync-agent. The API looks up whose athlete
 * it is and answers Strava straight away; the agent does the work.
 */
export const StravaEventMessage = envelope(
  'strava.event',
  z.object({
    object_type: z.enum(['activity', 'athlete']),
    aspect_type: z.enum(['create', 'update', 'delete']),
    /** Activity or athlete id, as a string (Strava ids outgrow 2^53 in time). */
    object_id: z.string(),
    athlete_id: z.string(),
    /** For athlete updates, `{ authorized: 'false' }` means the rider revoked access. */
    updates: z.record(z.string(), z.unknown()).default({}),
  }),
)
export type StravaEventMessage = z.infer<typeof StravaEventMessage>

/**
 * Import a rider's Strava history, one page per message: the agent handles
 * `page` and queues the next until Strava returns an empty page.
 */
export const StravaBackfillMessage = envelope(
  'strava.backfill',
  z.object({
    page: z.number().int().min(1),
    /** Only activities after this (the last sync), or everything when absent. */
    after: z.iso.datetime().nullable().default(null),
    /** Ties the pages of one run together, so a newer run can supersede it. */
    run_id: z.uuid(),
  }),
)
export type StravaBackfillMessage = z.infer<typeof StravaBackfillMessage>

/** ride-sync-agent added, changed or removed rides. The API relays it to open apps. */
export const RidesSyncedMessage = envelope(
  'rides.synced',
  z.object({
    source: z.literal('strava'),
    added: z.number().int(),
    updated: z.number().int(),
    removed: z.number().int(),
    /** True when a backfill run finished (or a sync failed): the status screen can stop waiting. */
    finished: z.boolean().default(false),
  }),
)
export type RidesSyncedMessage = z.infer<typeof RidesSyncedMessage>

/** Every message type known to the system. Agents narrow on `type`. */
export const QueueMessage = z.discriminatedUnion('type', [
  MediaProcessMessage,
  PhotoProcessedMessage,
  UsageChangedMessage,
  NotificationCreatedMessage,
  CaptureAudioMessage,
  ExportRequestedMessage,
  ExportReadyMessage,
  MediaOcrMessage,
  AiRequestedMessage,
  AiSuggestionReadyMessage,
  StravaEventMessage,
  StravaBackfillMessage,
  RidesSyncedMessage,
])
export type QueueMessage = z.infer<typeof QueueMessage>
export type QueueMessageType = QueueMessage['type']

/** Payload for a message type, for use when building a message. */
export type PayloadOf<T extends QueueMessageType> = Extract<QueueMessage, { type: T }>['payload']

export function buildMessage<T extends QueueMessageType>(
  type: T,
  userId: string,
  payload: PayloadOf<T>,
  id: string = crypto.randomUUID(),
): Extract<QueueMessage, { type: T }> {
  return {
    id,
    type,
    version: 1,
    user_id: userId,
    enqueued_at: new Date().toISOString(),
    payload,
  } as Extract<QueueMessage, { type: T }>
}
